import { useContext } from "react";
import { ExternalLink } from "lucide-react";
import { BuildingStore, Crown, Lock, UserShield, Card, Button, Clock3, FileText, LayoutGrid, ReceiptText, Wallet } from "../../ui";
import { getLocalized, getNotificationLink, getScopeLabel, getText, isAutomated } from "../utils/notificationHelpers";
import { Context } from "../../../context/Context";

const CATEGORY_ICON = {
  menu: BuildingStore,
  manager: UserShield,
  report: Crown,
  ticket: Lock,
  billing: ReceiptText,
  operations: Clock3,
};

// Automated notifications: an icon per type (backend services/notifications/notificationRegistry.js).
const TYPE_ICON = {
  "report.monthly": FileText,
  "report.daily": FileText,
  "report.compensation": FileText,
  "report.compensation.branch": FileText,
  "menu.updated": BuildingStore,
  "hours.updated": Clock3,
  "billing.wallet_topup": Wallet,
  "application.integration": LayoutGrid,
  "application.deactivated": LayoutGrid,
};

export default function NotificationCard({ notification, restaurants, t, pending = false, onMarkRead, onOpen }) {
  const { lang = "ar" } = useContext(Context);
  const automated = isAutomated(notification);
  const Icon = (automated && TYPE_ICON[notification.type]) || CATEGORY_ICON[notification.cat] || BuildingStore;
  const title = getLocalized(notification, "title", "titleEn", lang);
  const body = getLocalized(notification, "body", "bodyEn", lang);
  const label = getLocalized(notification, "lbl", "lblEn", lang);
  const time = getLocalized(notification, "time", "timeEn", lang);
  const sender = automated ? (getText(lang).automated.sender) : getLocalized(notification, "sender", "senderEn", lang);
  const scopeLabel = getScopeLabel({ restaurants, scope: notification.scope, lang });
  const link = getNotificationLink(notification);
  const automatedText = getText(lang).automated;

  return (
    <Card
      className={`lud-notif-card ${notification.unread ? "is-unread" : ""} ${pending ? "is-pending" : ""} ${automated ? "is-automated" : ""}`}
      onClick={() => onMarkRead(notification.id)}
    >
      {notification.unread ? (
        <span className="lud-notif-unread-dot" role="img" aria-label={lang === "ar" ? "غير مقروء" : "Unread"} />
      ) : null}

      <div className="lud-notif-card-header">
        <div className={`lud-notif-card-icon lud-notif-card-icon--${notification.cat}`}>
          <Icon size={18} />
        </div>

        <div className="lud-notif-card-info">
          <h3>{title}</h3>
          <div className="lud-notif-meta">
            <span className={`lud-notif-badge lud-notif-badge--${notification.cat}`}>{label}</span>
            <span>{time}</span>
          </div>
        </div>

        <span className={`lud-notif-scope lud-notif-scope--${notification.scope?.level || "owner"}`}>
          {notification.scope?.level === "branch" ? <BuildingStore size={12} /> : null}
          {notification.scope?.level === "owner-only" ? <UserShield size={12} /> : null}
          {(!notification.scope?.level || notification.scope?.level === "owner") ? <Crown size={12} /> : null}
          {scopeLabel}
          {notification.scope?.visibleToOwner === false ? <Lock size={12} /> : null}
        </span>
      </div>

      <p className="lud-notif-card-body">{body}</p>

      <div className="lud-notif-card-footer">
        <div className="lud-notif-sender">
          <span className="lud-notif-avatar">{sender?.charAt(0)}</span>
          <span>{automated ? sender : `${sender} · ${t.send.supportTeam}`}</span>
        </div>

        {link && onOpen ? (
          <Button
            type="button"
            variant="primary"
            appearance="soft"
            size="sm"
            icon={<ExternalLink size={14} />}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(notification);
            }}
          >
            {automatedText.open}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
